import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/utils-app";
import { LockKeyIcon as LockKey, EyeIcon as Eye, EyeSlashIcon as EyeSlash, WarningIcon as Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/** Reset-password page.
 *
 *  URL: `/reset-password?token=…`
 *  Validates that the token query-string is present, posts the new password
 *  to the backend, and on success uses the returned JWT to log the user in
 *  immediately so they don't have to re-type credentials.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUserAndToken } = useAuth();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const tokenMissing = !token;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (pwd !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: pwd,
      });
      // Backend issues a fresh JWT — slide the user straight into the app.
      if (data?.token) {
        // setUserAndToken handles localStorage + context; AuthContext exposes it
        // as part of its public API in this codebase.
        setUserAndToken?.(data, data.token);
      }
      toast.success("Password reset. You're signed in.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6 font-manrope">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img
            src="/logo-256.png"
            alt="Armstrong K9 Registry"
            className="h-28 w-28 rounded-full object-contain"
          />
        </div>
        <div
          className="bg-white border border-[#E5E5E5] rounded-xl p-8 shadow-[0_8px_30px_rgba(90,107,86,0.04)]"
          data-testid="reset-password-page"
        >
          <h1 className="font-spectral text-3xl mb-1 tracking-tight">Set a new password</h1>
          <p className="text-sm text-[#6B6B6B] mb-6">
            Choose something at least 6 characters long. You'll be signed in
            automatically once it's saved.
          </p>

          {tokenMissing && (
            <div
              className="mb-5 bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg p-4 text-sm text-[#991B1B] flex items-start gap-2"
              data-testid="reset-password-no-token"
            >
              <Warning size={16} weight="duotone" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Reset link missing</p>
                <p className="text-xs opacity-90 mt-1">
                  This page should be opened from the email link we sent. If your
                  link is broken, request a fresh one below.
                </p>
              </div>
            </div>
          )}

          {error && !tokenMissing && (
            <div
              className="mb-5 bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg p-4 text-sm text-[#991B1B] flex items-start gap-2"
              data-testid="reset-password-error"
            >
              <Warning size={16} weight="duotone" className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="pwd" className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                New password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="pwd"
                  type={show ? "text" : "password"}
                  required
                  minLength={6}
                  autoFocus
                  disabled={tokenMissing}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  data-testid="reset-password-pwd-input"
                  className="pr-10 rounded-lg p-3 focus:ring-2 focus:ring-[#0E0E0E]/20 focus:border-[#0E0E0E]"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  data-testid="reset-password-show-toggle"
                  aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6B6B] hover:text-ink transition-colors"
                >
                  {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm" className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Confirm new password
              </Label>
              <Input
                id="confirm"
                type={show ? "text" : "password"}
                required
                minLength={6}
                disabled={tokenMissing}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-password-confirm-input"
                className="mt-1 rounded-lg p-3 focus:ring-2 focus:ring-[#0E0E0E]/20 focus:border-[#0E0E0E]"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || tokenMissing}
              className="w-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full h-11"
              data-testid="reset-password-submit-btn"
            >
              <LockKey size={16} weight="duotone" className="mr-2" />
              {loading ? "Saving…" : "Save new password"}
            </Button>
          </form>

          <p className="text-sm text-center text-[#6B6B6B] mt-6">
            Need a fresh link?{" "}
            <Link to="/forgot-password" className="text-ink font-semibold" data-testid="reset-password-resend-link">
              Request another
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
